import { useId, useState, type ReactNode } from 'react';
import { RASCUNHO } from '@shared/lib/modo';

/*
  Os moldes.

  Inventario fechado de formas que um bloco pode assumir. Se um bloco novo nao
  couber em nenhuma delas, a conversa e sobre criar a nona, nao sobre escrever
  layout solto dentro da secao.
*/

/**
 * Lista curta separada por ponto, que so quebra nos pontos.
 *
 * "Farmacia · Seguranca do Paciente · Qualidade e Melhoria" quebrava no
 * celular entre "Qualidade e" e "Melhoria", e o item partido parecia dois.
 * Aqui cada item fica inteiro na linha, e a quebra so acontece depois de um
 * ponto.
 */
export function ComPontos({ texto }: { texto: string }) {
  const itens = texto.split(' · ');
  return (
    <>
      {itens.map((item, indice) => (
        <span key={item}>
          <span className="whitespace-nowrap">{item}</span>
          {indice < itens.length - 1 ? ' · ' : null}
        </span>
      ))}
    </>
  );
}

/*
  Texto curto de card (duas a quatro linhas) usa text-balance, e nao
  text-pretty: o pretty do Chrome desiste em parte dos casos e deixava
  "aprendizagem." sozinho na ultima linha. O balance divide as palavras por
  igual e nunca deixa palavra sozinha. Texto longo continua com pretty, que
  e o padrao global em index.css.
*/

/** Texto corrido, em serifa. */
export function Prosa({ children }: { children: ReactNode }) {
  return <div className="prosa space-y-5">{children}</div>;
}

/** Grade de cartoes sem caixa. Fio em cima, respiro em volta. */
export function GradeDeCartoes({ itens }: { itens: { titulo: string; texto: string }[] }) {
  return (
    <ul className="grid gap-x-10 gap-y-8 sm:grid-cols-2">
      {itens.map((item) => (
        <li key={item.titulo} className="border-t border-linha-forte pt-4">
          <h3 className="display-item">{item.titulo}</h3>
          <p className="mt-2 text-apoio leading-relaxed text-balance text-tinta-suave">{item.texto}</p>
        </li>
      ))}
    </ul>
  );
}

/** Sequencia numerada. As etapas do curso, o ritmo de um cenario. */
export function Etapas({
  itens,
  desdobramento,
}: {
  itens: { titulo: string; texto: string }[];
  /*
    Zoom em um dos itens.

    Entra dentro do item, alinhado com o titulo dele, e nao embaixo da lista.
    A hierarquia visual precisa dizer sozinha que uma coisa esta contida na
    outra: duas sequencias empilhadas no mesmo recuo o leitor tenta parear
    item a item, mesmo quando nao tem relacao nenhuma entre elas.
  */
  desdobramento?: { indice: number; conteudo: ReactNode };
}) {
  return (
    <ol className="divide-y divide-linha border-t border-linha">
      {itens.map((item, indice) => (
        <li key={item.titulo} className="flex gap-6 py-6">
          <span className="rotulo mt-1.5 shrink-0 tabular-nums">
            {String(indice + 1).padStart(2, '0')}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="display-item">{item.titulo}</h3>
            <p className="mt-1.5 text-apoio leading-relaxed text-balance text-tinta-suave">{item.texto}</p>
            {desdobramento?.indice === indice ? (
              <div className="mt-6">{desdobramento.conteudo}</div>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Regua de fases, com foto.
 *
 * Tres momentos lado a lado, na ordem em que acontecem. A foto aqui nao e
 * ilustracao: e o unico lugar da pagina onde a aviacao e a saude aparecem na
 * mesma imagem, que e a tese do curso dita sem texto.
 *
 * Foto que ainda nao existe vira buraco marcado em rascunho e some na versao
 * publicada, porque card torto comunica curso improvisado.
 */
export function Fases({
  itens,
}: {
  itens: { titulo: string; texto: string; imagem?: string; alt?: string }[];
}) {
  return (
    <ol className="divide-y divide-linha border-y border-linha">
      {itens.map((item) => (
        <li key={item.titulo} className="flex flex-col gap-4 py-5 sm:flex-row sm:items-center">
          <div className="sm:w-2/5 sm:shrink-0">
            <Foto imagem={item.imagem} alt={item.alt} />
          </div>
          <div className="min-w-0">
            <h4 className="text-titulo font-medium">{item.titulo}</h4>
            <p className="mt-1 text-apoio leading-relaxed text-balance text-tinta-suave">{item.texto}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Uma foto 16:9, ou o buraco dela.
 *
 * `overflow-hidden` nao e decorativo: sem ele a imagem escapa da moldura na
 * primeira vez que alguem puser um `scale` no hover.
 */
function Foto({
  imagem,
  alt,
  depende,
  proporcao = 'aspect-video',
  enquadramento,
}: {
  imagem?: string | undefined;
  alt?: string | undefined;
  /* De quem ou do que a foto que falta depende. So aparece em rascunho. */
  depende?: string | undefined;
  /* 16:9 para cena, quadrado para retrato. */
  proporcao?: string;
  /* Ponto da foto que fica no centro do corte, como em object-position. */
  enquadramento?: string | undefined;
}) {
  if (imagem) {
    return (
      <div className={`${proporcao} overflow-hidden bg-tinta/10`}>
        <img src={imagem} alt={alt ?? ''} loading="lazy"
          className="h-full w-full object-cover"
          style={enquadramento ? { objectPosition: enquadramento } : undefined}
        />
      </div>
    );
  }

  if (!RASCUNHO) return null;

  return (
    <div
      className={`${proporcao} flex flex-col items-center justify-center gap-1.5 border border-dashed border-sinal bg-sinal-fundo/50 px-4 text-center`}
    >
      <span className="rotulo text-sinal">Foto pendente</span>
      {depende ? <span className="text-miudo text-tinta-fraca">Depende de: {depende}</span> : null}
    </div>
  );
}

/**
 * As competencias, cada uma com sua foto e suas ferramentas.
 *
 * Duas colunas em vez de lista: a competencia e a unidade de comparacao, e em
 * lista vertical com foto cada linha ficava do tamanho de uma tela.
 */
export function GradeDeCompetencias({
  itens,
}: {
  itens: {
    competencia: string;
    texto: string;
    ferramentas: string[];
    imagem?: string;
    alt?: string;
    depende?: string;
  }[];
}) {
  return (
    <ul className="grid gap-x-8 gap-y-10 sm:grid-cols-2">
      {itens.map((item) => (
        <li key={item.competencia}>
          <Foto imagem={item.imagem} alt={item.alt} depende={item.depende} />
          <h3 className="display-item mt-4 border-t border-linha-forte pt-3">{item.competencia}</h3>
          <p className="mt-1.5 text-apoio leading-relaxed text-balance text-tinta-suave">{item.texto}</p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {item.ferramentas.map((ferramenta) => (
              <li
                key={ferramenta}
                className="border border-linha-forte px-2.5 py-1 text-miudo"
              >
                {ferramenta}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/** Pares rotulo e valor, com fio pontilhado ligando os dois. */
export function Ficha({ linhas }: { linhas: { rotulo: string; valor: ReactNode }[] }) {
  return (
    <dl className="border-t border-linha">
      {/*
        A chave leva o indice junto porque o rotulo repete: na tabela de
        descontos, tres linhas diferentes valem 10%. So com o rotulo, o React
        trata as tres como a mesma linha e pode sumir com duas.
      */}
      {linhas.map((linha, indice) => (
        <div
          key={`${linha.rotulo}-${indice}`}
          className="border-b border-linha py-3 sm:flex sm:items-baseline sm:gap-4 sm:py-3.5"
        >
          <dt className="rotulo normal-case tracking-normal sm:w-32 sm:shrink-0">
            {linha.rotulo}
          </dt>
          <dd className="mt-1 text-apoio text-balance text-tinta sm:mt-0 sm:flex-1">
            {linha.valor}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Abre e fecha. As duvidas. */
export function Sanfona({ itens }: { itens: { pergunta: string; resposta: ReactNode }[] }) {
  const base = useId();
  const [aberta, setAberta] = useState<string | null>(null);

  return (
    <ul className="border-t border-linha">
      {itens.map((item) => {
        const identificador = `${base}-${item.pergunta}`;
        const estaAberta = aberta === item.pergunta;
        return (
          <li key={item.pergunta} className="border-b border-linha">
            <button
              type="button"
              aria-expanded={estaAberta}
              aria-controls={identificador}
              onClick={() => setAberta(estaAberta ? null : item.pergunta)}
              className="group flex w-full items-baseline justify-between gap-6 py-4 text-left"
            >
              <span className="display-item font-normal transition-colors group-hover:text-destaque">
                {item.pergunta}
              </span>
              <span
                aria-hidden
                className={`shrink-0 text-tinta-fraca transition-transform duration-300 ${
                  estaAberta ? 'rotate-45' : ''
                }`}
              >
                +
              </span>
            </button>
            {estaAberta ? (
              <div
                id={identificador}
                className="max-w-xl pb-5 text-apoio leading-relaxed text-tinta-suave"
              >
                {item.resposta}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** Grade de pessoas. */
export function GradeDePessoas({
  pessoas,
}: {
  pessoas: {
    nome: string;
    papel: string;
    eixos: string;
    bio: string;
    contribuicao: string;
    linkedin: string;
    foto?: string | undefined;
    alt?: string | undefined;
    enquadramento?: string | undefined;
  }[];
}) {
  return (
    <ul className="grid gap-x-8 gap-y-10 sm:grid-cols-2">
      {pessoas.map((pessoa) => (
        <li key={pessoa.nome}>
          {/*
            Retrato em quadrado, nao em 16:9: rosto cortado na horizontal fica
            em faixa. Quatro das cinco fotos ainda nao existem, e em rascunho
            o buraco aparece marcado.
          */}
          <div className="max-w-44">
            <Foto
              imagem={pessoa.foto}
              alt={pessoa.alt}
              proporcao="aspect-square"
              enquadramento={pessoa.enquadramento}
            />
          </div>
          <h3 className="display-item mt-4">{pessoa.nome}</h3>
          <p className="rotulo mt-1">{pessoa.papel}</p>
          <p className="mt-2 text-miudo text-balance text-tinta-fraca">
            <ComPontos texto={pessoa.eixos} />
          </p>
          <p className="mt-3 text-apoio leading-relaxed text-pretty text-tinta-suave">{pessoa.bio}</p>
          <p className="mt-2 text-apoio leading-relaxed text-pretty text-tinta-suave">
            {pessoa.contribuicao}
          </p>
          <a
            href={pessoa.linkedin}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex min-h-11 items-center text-apoio text-destaque underline underline-offset-2 sm:min-h-0"
          >
            Perfil profissional
          </a>
        </li>
      ))}
    </ul>
  );
}
